// نرمال‌سازی مخاطبین - منطق خالص، بدون react-native و بدون expo.
// عمداً از هیچ ماژول نیتیوی import نمی‌کنه تا با `npm test` مستقیم اجرا بشه.

/** زیرمجموعه‌ی ساختاریِ Contacts.Contact که واقعاً استفاده می‌کنیم. */
export interface DeviceContactLike {
  id?: string;
  name?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  nickname?: string;
  company?: string;
  jobTitle?: string;
  note?: string;
  imageAvailable?: boolean;
  birthday?: { day?: number; month?: number; year?: number };
  emails?: { email?: string }[];
  phoneNumbers?: { number?: string; digits?: string }[];
  relationships?: { label?: string; name?: string }[];
  /** iOS only */
  socialProfiles?: { service?: string; username?: string; url?: string; userId?: string; label?: string }[];
  instantMessageAddresses?: { service?: string; username?: string; label?: string }[];
  urlAddresses?: { url?: string; label?: string }[];
  [k: string]: unknown;
}

export interface Relationship { label: string; name: string | null; }

/** یک ورودی شبکه‌ی اجتماعی، از هر سه منبعِ ممکن (پایین رو ببین). */
export interface SocialProfile {
  service: string;
  username: string | null;
  url: string | null;
  source: 'social' | 'im' | 'url';
}

/**
 * یک مخاطبِ خوانده‌شده از دفترچه‌ی گوشی، نرمال‌شده.
 *
 * اسمش «payload» مانده از روزی که همین شیء به RPC `sync_contacts` می‌رفت.
 * آن تابع در مایگریشن 0122 حذف شد و این ساختار دیگر **هیچ‌جا به سرور
 * نمی‌رود**: فقط `phone_keys` و `emails` هش می‌شوند و هشِ آن‌ها به
 * `match_contacts` می‌رود؛ بقیه‌ی فیلدها روی خودِ گوشی می‌مانند
 * (`features/contacts/localBook.ts`).
 */
export interface ContactPayload {
  device_id: string;
  display_name: string;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  nickname: string | null;
  company: string | null;
  job_title: string | null;
  emails: string[];
  phones: string[];
  phone_keys: string[];
  relationships: Relationship[];
  social_profiles: SocialProfile[];
  birthday: string | null;
  note: string | null;
  image_available: boolean;
  raw: Record<string, unknown>;
}

const digitsOf = (raw: string): string => raw.replace(/\D/g, '');

/**
 * شماره → شکلِ بین‌المللی (E.164مانند). یه heuristic ساده‌ست، نه libphonenumber:
 * مچینگ همیشه با {@link phoneKey} انجام میشه، ولی چیزی که **ذخیره** میشه همینه.
 *
 * از WHOSUP-162 خروجی همیشه با «+» شروع میشه (یا null): شماره‌ای که کد کشور
 * نداره، کد کشورِ خودِ کاربر رو می‌گیره ({@link phoneRepo.myCallingCode} —
 * از شهرِ پروفایلش). صفرِ اولِ فرمتِ ملی هم برداشته میشه: «0912…» با کد ۹۸
 * میشه «98912…+»، همون کاری که موقعِ شماره‌گیریِ بین‌المللی می‌کنی.
 */
export function normalizePhone(raw: string | null | undefined, defaultCallingCode = '1'): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  const d = digitsOf(trimmed);
  if (d.length < 7) return null; // داخلی/کد کوتاه - شماره‌ی واقعی نیست
  if (trimmed.startsWith('+')) return `+${d}`;
  if (d.startsWith('00')) return `+${d.slice(2)}`;
  if (d.startsWith('0')) return `+${defaultCallingCode}${d.slice(1)}`; // فرمت ملی با صفرِ خط
  if (d.length >= 11) return `+${d}`; // به این بلندی، کد کشور به احتمال زیاد داخلشه
  return `+${defaultCallingCode}${d}`;
}

/**
 * کلید مچ: ۱۰ رقم آخر. باعث میشه «+1 613 555 0123» و «613-555-0123» و
 * «001 613 555 0123» به یه کلید برسن. باید دقیقاً قرینه‌ی `public.phone_key` در SQL باشه.
 */
export function phoneKey(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const d = digitsOf(raw);
  if (d.length < 7) return null;
  return d.slice(-10);
}

/** حداقلی که برای فهمیدنِ «این دو ردیف یه آدم‌اند» لازمه. */
export interface MergeableContact {
  id: number;
  displayName: string;
  phones: string[];
  emails: string[];
  isFriend?: boolean;
  matchedUserId?: string | null;
  matched?: unknown;
}

/**
 * مخاطبینِ تکراری رو یکی می‌کنه.
 *
 * دو کلید هست و هر کدوم به تنهایی کافیه: **شماره** و **کاربر**. یه شماره
 * هیچ‌وقت دو کاربر نداره، ولی یه کاربر می‌تونه چند شماره داشته باشه — پس
 * همون آدم می‌تونه با دو شماره‌ی مختلف دو بار توی دفترچه‌ی گوشی باشه.
 *
 * سرور همین قانون رو روی ردیف‌های خودش دارد (مایگریشن 0048)، ولی فهرستِ
 * مخاطبین ترکیبی از ردیف‌های سرور و دفترچه‌ی محلیِ گوشیه و تکرار می‌تونه
 * از سمتِ گوشی بیاد. بدون این، یه نفر دو بار توی فهرست دیده می‌شد.
 *
 * ردیفِ ماندگار اونیه که شناسه‌ی واقعیِ سرور داره (شناسه‌ی منفی یعنی فقط
 * توی گوشیه)؛ وگرنه اولی. شماره‌ها و ایمیل‌ها اجتماع می‌شن تا هیچ راهِ
 * رسیدنی گم نشه.
 */
export function mergeDuplicateContacts<T extends MergeableContact>(rows: T[]): T[] {
  const out: T[] = [];
  const byUser = new Map<string, number>();
  const byPhone = new Map<string, number>();

  const indexOf = (r: T): number | undefined => {
    if (r.matchedUserId) {
      const i = byUser.get(r.matchedUserId);
      if (i !== undefined) return i;
    }
    for (const k of uniq(r.phones.map(phoneKey))) {
      const i = byPhone.get(k);
      if (i !== undefined) return i;
    }
    return undefined;
  };

  const remember = (i: number) => {
    const r = out[i];
    if (r.matchedUserId) byUser.set(r.matchedUserId, i);
    for (const k of uniq(r.phones.map(phoneKey))) byPhone.set(k, i);
  };

  for (const row of rows) {
    const at = indexOf(row);
    if (at === undefined) {
      out.push({ ...row });
      remember(out.length - 1);
      continue;
    }

    const kept = out[at];
    const winner = kept.id > 0 || row.id <= 0 ? kept : row;
    const loser = winner === kept ? row : kept;
    out[at] = {
      ...winner,
      displayName: winner.displayName || loser.displayName,
      phones: uniq([...winner.phones, ...loser.phones]),
      emails: uniq([...winner.emails, ...loser.emails]),
      isFriend: !!(winner.isFriend || loser.isFriend),
      // مچ همیشه جفتی منتقل میشه: شناسه‌ی کاربر بدونِ پروفایلش یعنی ردیفی
      // که می‌گه «عضوه» ولی هیچی ازش نشون نمی‌ده.
      ...(winner.matchedUserId
        ? {}
        : { matchedUserId: loser.matchedUserId, matched: loser.matched }),
    } as T;
    remember(at);
  }

  return out;
}

/**
 * «این شماره رو از قبل روی یه مخاطبِ دیگه داری».
 *
 * از تریگرِ مایگریشن 0048 میاد. صفحه‌ها ازش استفاده می‌کنن تا به‌جای
 * «خطا - دوباره امتحان کن»، بگن دقیقاً چی شد. قبلاً توی contactsRepoِ
 * موبایل بود؛ از وقتی وب هم فرمِ ویرایش دارد (WHOSUP-144) این‌جاست.
 */
export const isDuplicatePhoneError = (e: unknown): boolean =>
  typeof (e as { message?: unknown })?.message === 'string'
  && (e as { message: string }).message.includes('contact_duplicate_phone');

export function normalizeEmail(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const e = raw.trim().toLowerCase();
  return e.includes('@') && e.length > 2 ? e : null;
}

/** `{ day, month, year }` اکسپو → `YYYY-MM-DD` (ماه از صفر شروع میشه). */
export function formatBirthday(b: DeviceContactLike['birthday']): string | null {
  if (!b || b.day == null || b.month == null) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  const y = b.year != null ? String(b.year).padStart(4, '0') : '0000';
  return `${y}-${pad(b.month + 1)}-${pad(b.day)}`;
}

const uniq = (xs: (string | null)[]): string[] => [...new Set(xs.filter((x): x is string => !!x))];

const clean = (v: unknown): string | null => {
  const s = typeof v === 'string' ? v.trim() : '';
  return s.length ? s : null;
};

/** فیلدهای حجیم/بی‌فایده‌ای که توی `raw` ذخیره نمی‌شن. */
const RAW_SKIP = new Set(['image', 'rawImage', 'imageAvailable', 'id']);

/** دامنه‌های شناخته‌شده → اسم سرویس. بقیه با هاست خودشون ثبت می‌شن. */
const SERVICE_HOSTS: [RegExp, string][] = [
  [/(^|\.)instagram\.com$/, 'instagram'],
  [/(^|\.)(twitter|x)\.com$/, 'twitter'],
  [/(^|\.)facebook\.com$|(^|\.)fb\.(com|me)$/, 'facebook'],
  [/(^|\.)linkedin\.com$/, 'linkedin'],
  [/(^|\.)(t\.me|telegram\.(me|org))$/, 'telegram'],
  [/(^|\.)(wa\.me|whatsapp\.com)$/, 'whatsapp'],
  [/(^|\.)tiktok\.com$/, 'tiktok'],
  [/(^|\.)(youtube\.com|youtu\.be)$/, 'youtube'],
  [/(^|\.)github\.com$/, 'github'],
  [/(^|\.)snapchat\.com$/, 'snapchat'],
  [/(^|\.)threads\.(net|com)$/, 'threads'],
];

/** از یه URL، اسم سرویس و نام کاربری رو درمیاره. برای ورودی نامعتبر null میده. */
export function parseSocialUrl(raw: string | null | undefined): { service: string; username: string | null; url: string } | null {
  const s = clean(raw);
  if (!s) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(s) ? s : `https://${s}`;
  let host: string;
  let path: string;
  try {
    const u = new URL(withScheme);
    host = u.hostname.toLowerCase().replace(/^www\./, '');
    path = u.pathname;
  } catch {
    return null;
  }
  if (!host.includes('.')) return null;
  const service = SERVICE_HOSTS.find(([re]) => re.test(host))?.[1] ?? host;
  const seg = path.split('/').filter(Boolean).pop() ?? null;
  const username = seg ? decodeURIComponent(seg).replace(/^@/, '') : null;
  return { service, username: username || null, url: s };
}

export function extractRelationships(c: DeviceContactLike): Relationship[] {
  return (c.relationships ?? [])
    .map((r) => ({ label: clean(r?.label) ?? '', name: clean(r?.name) }))
    .filter((r) => r.label !== '' || r.name !== null);
}

/**
 * شبکه‌های اجتماعی از سه منبع جمع میشن، چون هر پلتفرم جای دیگه‌ای نگه‌شون می‌داره:
 *   socialProfiles            - فقط iOS
 *   instantMessageAddresses   - iOS و اندروید (اکثر هندل‌های تلگرام/واتساپ اینجان)
 *   urlAddresses              - iOS و اندروید (لینک پروفایل‌ها)
 * روی اندروید معمولاً فقط دوتای آخر پر می‌شن.
 */
export function extractSocialProfiles(c: DeviceContactLike): SocialProfile[] {
  const out: SocialProfile[] = [];

  for (const p of c.socialProfiles ?? []) {
    const service = clean(p?.service) ?? clean(p?.label);
    const username = clean(p?.username) ?? clean(p?.userId);
    const url = clean(p?.url);
    if (!service && !username && !url) continue;
    const fromUrl = url ? parseSocialUrl(url) : null;
    out.push({
      service: (service ?? fromUrl?.service ?? 'unknown').toLowerCase(),
      username: username ?? fromUrl?.username ?? null,
      url: url ?? null,
      source: 'social',
    });
  }

  for (const p of c.instantMessageAddresses ?? []) {
    const service = clean(p?.service) ?? clean(p?.label);
    const username = clean(p?.username);
    if (!service && !username) continue;
    out.push({
      service: (service ?? 'unknown').toLowerCase(), username, url: null, source: 'im',
    });
  }

  for (const p of c.urlAddresses ?? []) {
    const parsed = parseSocialUrl(p?.url);
    if (!parsed) continue;
    out.push({ service: parsed.service, username: parsed.username, url: parsed.url, source: 'url' });
  }

  // حذف تکراری‌ها (مثلاً وقتی هم socialProfile هست هم لینکش توی urlAddresses)
  const seen = new Set<string>();
  return out.filter((p) => {
    const k = `${p.service}|${p.username ?? ''}|${p.url ?? ''}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export function toPayload(c: DeviceContactLike, defaultCallingCode = '1'): ContactPayload | null {
  const deviceId = clean(c.id);
  if (!deviceId) return null;

  const emails = uniq((c.emails ?? []).map((e) => normalizeEmail(e?.email)));
  const phonesRaw = (c.phoneNumbers ?? []).map((p) => p?.number ?? p?.digits ?? null);
  const phones = uniq(phonesRaw.map((p) => normalizePhone(p, defaultCallingCode)));
  const phoneKeys = uniq(phonesRaw.map(phoneKey));

  const relationships = extractRelationships(c);
  const socialProfiles = extractSocialProfiles(c);

  const displayName = clean(c.name)
    ?? clean([clean(c.firstName), clean(c.lastName)].filter(Boolean).join(' '))
    ?? '';
  // مخاطبی که نه اسم داره نه هیچ راه ارتباطی، ارزش ذخیره‌سازی نداره
  if (!displayName && !emails.length && !phones.length && !socialProfiles.length) return null;

  const raw: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(c)) {
    if (!RAW_SKIP.has(k) && v != null) raw[k] = v;
  }

  return {
    device_id: deviceId,
    display_name: displayName,
    first_name: clean(c.firstName),
    middle_name: clean(c.middleName),
    last_name: clean(c.lastName),
    nickname: clean(c.nickname),
    company: clean(c.company),
    job_title: clean(c.jobTitle),
    emails,
    phones,
    phone_keys: phoneKeys,
    relationships,
    social_profiles: socialProfiles,
    birthday: formatBirthday(c.birthday),
    note: clean(c.note),
    image_available: c.imageAvailable === true,
    raw,
  };
}

/** تبدیل لیست گوشی به payload، با حذف ردیف‌های بی‌ارزش و device_idهای تکراری. */
export function toPayloads(list: DeviceContactLike[], defaultCallingCode = '1'): ContactPayload[] {
  const seen = new Set<string>();
  const out: ContactPayload[] = [];
  for (const c of list) {
    const p = toPayload(c, defaultCallingCode);
    if (!p || seen.has(p.device_id)) continue;
    seen.add(p.device_id);
    out.push(p);
  }
  return out;
}

export function chunk<T>(arr: T[], size: number): T[][] {
  if (size < 1) throw new Error('chunk size must be >= 1');
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}
